import {
  Box1,
  Briefcase,
  Brush3,
  Buildings,
  Buildings2,
  Calculator,
  Calendar,
  Chart,
  Chart21,
  Clipboard,
  Copy,
  DeviceMessage,
  DocumentCode,
  DollarCircle,
  Element3,
  I3Square,
  Monitor,
  Notepad,
  Notepad2,
  Notification,
  Profile2User,
  ReceiptItem,
  Setting2,
  SmsTracking,
  TruckFast,
  User,
  UserSearch,
  UserSquare,
} from 'iconsax-react';
import type { ReactElement } from 'react';
import { Navigate } from 'react-router-dom';

import Enums from '@core/types/api/Enums';
import type { UserInfo } from '@core/types/api/UserInfo/UserInfo';
import { hasValue } from '@core/utils/equality';

import { ErrorPage } from './components/Error/ErrorPage';
import { ObfuscatedActualsPage } from './pages/Actuals/ObfuscatedActualsPage';
import { PermissionDeniedPage } from './pages/PermissionDeniedPage/PermissionDeniedPage';
import { ObfuscatedPlanningPage } from './pages/Planning/ObfuscatedPlanningPage';
import {
  AIChatPage,
  ActivitiesView,
  ActivityDetailsPage,
  ActualsPage,
  AdminPage,
  AdminUserManagementPage,
  ApprovalFlowPage,
  BacklogSettings,
  CompanyDetailsPage,
  CompanyList,
  CompetitorsList,
  ContactDetailsPage,
  ContactList,
  Dashboard,
  DispatchCustomerList,
  EmailAccountPage,
  EmailCcBccPage,
  EmailConfigLayout,
  EmailSignaturesPage,
  EscalationManagerList,
  ForecastList,
  MarginConfiguration,
  MaterialDetailsPage,
  MaterialList,
  MixDetailsPage,
  MixList,
  NotificationPreferencesPage,
  PlanningPage,
  PlantDetailsPage,
  PlantList,
  ProductDetailsPage,
  ProductList,
  ProjectConfigDetailsPage,
  ProjectConfigList,
  ProjectDetailsPage,
  ProjectList,
  ProjectSettings,
  QuoteConfigDetailsPage,
  QuoteConfigList,
  QuoteDetailsPage,
  QuoteList,
  QuotePDFTemplateConfiguration,
  Refer,
  StatusConfiguration,
  SystemRemindersList,
  TenantConfiguration,
  TierPricingPage,
  TierPricingSettings,
  TruckingList,
} from './utils/LazyImports';
import { withSlabErrorBoundary } from './utils/withSlabErrorBoundary';

export type SlabRoute = {
  /** Role permissions required to access this route. */
  permissions?: Enums.Permission[];
  /** User roles which grant a user access to this route: a user must have
   *  at least one. An undefined value is equivalent to listing all roles.
   */
  allowedRoles?: Enums.RoleName[];
  /** URL path (excluding hostname) */
  path: string;
  /** The element rendered at that route */
  main: () => ReactElement;
  /** Icon to display (if it is in the NavBar) */
  icon?: (color?: string) => ReactElement | null;
  /** Name to display (if it is in the NavBar) */
  name?: string;
  /** Feature flags required to access this route. */
  requiredFlags?: Enums.FeatureFlagName[];

  subRoutes?: SlabRoutesSection[];

  /** Feature flag that marks the feature as new in the menu */
  newFeature?: boolean;

  /** If set, show the "teaser" content instead of the page, unless flags for disableObfuscation are set */
  obfuscatedMain?: () => ReactElement;

  /** Feature flags that, if obfuscatedMain is set, disable obfuscation and show the regular content */
  disableObfuscation?: Enums.FeatureFlagName[];

  /** If provided, the route is hidden from navigation unless this returns true. */
  visible?: (userInfo: UserInfo) => boolean;
};

export type SlabRoutesSection = {
  name: string;
  children: SlabRoute[];
};

export const slabActualsRouter: SlabRoutesSection[] = [
  {
    name: 'Actuals',
    children: [
      {
        name: 'Dashboard Overview',
        path: '',
        main: (): ReactElement => <ActualsPage />,
      },
      {
        name: 'Project Details',
        path: 'project-details',
        main: (): ReactElement => <ActualsPage />,
      },
      {
        name: 'Customer Performance',
        path: 'customer-performance',
        main: (): ReactElement => <ActualsPage />,
      },
      {
        name: 'Alerts & Actions',
        path: 'alerts',
        main: (): ReactElement => <ActualsPage />,
      },
    ],
  },
];

export const slabCompetitorsRouter: SlabRoutesSection[] = [
  {
    name: 'Competitors',
    children: [
      {
        name: 'List',
        path: '',
        main: (): ReactElement => <CompetitorsList />,
      },
      {
        name: 'Map',
        path: 'map',
        main: (): ReactElement => <CompetitorsList />,
      },
    ],
  },
];

export const slabTierPricingRouter: SlabRoutesSection[] = [
  {
    name: 'Tier Pricing',
    children: [
      {
        name: 'Tier Pricing Configs',
        path: '',
        main: () => <TierPricingPage />,
      },
      {
        name: 'Tier pricing',
        path: 'tier-pricing',
        main: () => <TierPricingPage />,
      },
      {
        name: 'Price Lists',
        path: 'price-lists',
        main: () => <TierPricingPage />,
      },
      {
        name: 'Fixed Pricing',
        path: 'fixed-pricing',
        main: () => <TierPricingPage />,
      },
      {
        requiredFlags: [Enums.FeatureFlagName.FeatureFlagDiscounts],
        name: 'Discounts',
        path: 'discounts',
        main: () => <TierPricingPage />,
      },
    ],
  },
];

export const slabDispatchCustomersRouter: SlabRoutesSection[] = [
  {
    name: 'Dispatch Customers',
    children: [
      {
        name: 'Unlinked',
        path: '',
        main: (): ReactElement => <DispatchCustomerList />,
      },
      {
        name: 'Unmerged',
        path: 'unmerged',
        main: (): ReactElement => <DispatchCustomerList />,
      },
      {
        name: 'Linked & Merged',
        path: 'linked',
        main: (): ReactElement => <DispatchCustomerList />,
      },
      {
        name: 'Ignored',
        path: 'ignored',
        main: (): ReactElement => <DispatchCustomerList />,
      },
    ],
  },
];

export const slabEmailConfigRouterSections: SlabRoutesSection[] = [
  {
    name: 'Email Config',
    children: [
      {
        path: '',
        name: 'Email Account',
        icon: (color?: string): ReactElement => <DeviceMessage variant='TwoTone' color={color} />,
        main: (): ReactElement => <EmailAccountPage />,
      },
      {
        path: 'signatures',
        name: 'Signatures',
        icon: (color?: string): ReactElement => <SmsTracking variant='TwoTone' color={color} />,
        main: (): ReactElement => <EmailSignaturesPage />,
      },
      {
        path: 'cc-bcc',
        name: 'CC / BCC',
        icon: (color?: string): ReactElement => <Copy variant='TwoTone' color={color} />,
        main: (): ReactElement => <EmailCcBccPage />,
      },
      {
        path: '*',
        main: (): ReactElement => <Navigate to='' />,
      },
    ],
  },
];

export const slabAdminRouterSections: SlabRoutesSection[] = [
  {
    name: 'Admin',
    children: [
      {
        requiredFlags: [Enums.FeatureFlagName.FeatureFlagReminderSystemSettings],
        allowedRoles: [Enums.RoleName.SlabstackAdministrator, Enums.RoleName.ITAdministrator],
        path: 'tenant-configuration',
        name: 'Tenant Configuration',
        icon: (color?: string): ReactElement => <Buildings2 variant='TwoTone' color={color} />,
        main: () => <TenantConfiguration />,
      },
      {
        allowedRoles: [Enums.RoleName.SlabstackAdministrator, Enums.RoleName.ITAdministrator],
        path: '',
        name: 'User Management',
        icon: (color?: string): ReactElement => <Profile2User variant='TwoTone' color={color} />,
        main: () => <AdminUserManagementPage />,
        subRoutes: [
          {
            name: 'User Management Tabs',
            children: [
              {
                allowedRoles: [
                  Enums.RoleName.SlabstackAdministrator,
                  Enums.RoleName.ITAdministrator,
                ],
                path: '',
                main: () => <AdminUserManagementPage />,
              },
              {
                allowedRoles: [
                  Enums.RoleName.SlabstackAdministrator,
                  Enums.RoleName.ITAdministrator,
                ],
                path: 'markets',
                main: () => <AdminUserManagementPage />,
              },
              {
                allowedRoles: [
                  Enums.RoleName.SlabstackAdministrator,
                  Enums.RoleName.ITAdministrator,
                ],
                path: 'roles',
                main: () => <AdminUserManagementPage />,
              },
              {
                allowedRoles: [
                  Enums.RoleName.SlabstackAdministrator,
                  Enums.RoleName.ITAdministrator,
                ],
                path: 'divisions',
                main: () => <AdminUserManagementPage />,
              },
            ],
          },
        ],
      },
      {
        allowedRoles: [Enums.RoleName.SlabstackAdministrator, Enums.RoleName.ITAdministrator],
        path: 'quote-approvals',
        name: 'Quote Approval Workflows',
        icon: (color?: string): ReactElement => <ReceiptItem variant='TwoTone' color={color} />,
        main: () => <ApprovalFlowPage />,
      },
      {
        requiredFlags: [Enums.FeatureFlagName.FeatureFlagTierPricing],
        allowedRoles: [Enums.RoleName.SlabstackAdministrator, Enums.RoleName.ITAdministrator],
        path: 'tier-pricing',
        name: 'Tier Pricing',
        icon: (color?: string): ReactElement => <DollarCircle variant='TwoTone' color={color} />,
        main: () => <TierPricingSettings />,
      },
      {
        requiredFlags: [Enums.FeatureFlagName.FeatureFlagReminderSystemSettings],
        allowedRoles: [Enums.RoleName.SlabstackAdministrator, Enums.RoleName.ITAdministrator],
        path: 'system-reminders',
        name: 'System Reminders',
        icon: (color?: string): ReactElement => <Notification variant='TwoTone' color={color} />,
        main: () => <SystemRemindersList />,
      },
      {
        allowedRoles: [Enums.RoleName.SlabstackAdministrator, Enums.RoleName.ITAdministrator],
        path: 'margin-configuration',
        name: 'Margin Configuration',
        icon: (color?: string): ReactElement => <Monitor variant='TwoTone' color={color} />,
        main: () => <MarginConfiguration />,
      },
      {
        allowedRoles: [Enums.RoleName.SlabstackAdministrator, Enums.RoleName.ITAdministrator],
        path: 'status-configuration',
        name: 'Status Configuration',
        icon: (color?: string): ReactElement => <Clipboard variant='TwoTone' color={color} />,
        main: () => <StatusConfiguration />,
        subRoutes: [
          {
            name: 'Status Configuration Tabs',
            children: [
              {
                allowedRoles: [
                  Enums.RoleName.SlabstackAdministrator,
                  Enums.RoleName.ITAdministrator,
                ],
                path: 'quotes',
                main: () => <StatusConfiguration />,
              },
              {
                allowedRoles: [
                  Enums.RoleName.SlabstackAdministrator,
                  Enums.RoleName.ITAdministrator,
                ],
                path: 'projects',
                main: () => <StatusConfiguration />,
              },
            ],
          },
        ],
      },
      {
        allowedRoles: [Enums.RoleName.SlabstackAdministrator],
        path: 'quote-pdf-template-configuration',
        name: 'Quote PDF Templates',
        icon: (color?: string): ReactElement => <DocumentCode variant='TwoTone' color={color} />,
        main: () => <QuotePDFTemplateConfiguration />,
      },
      {
        allowedRoles: [Enums.RoleName.SlabstackAdministrator, Enums.RoleName.ITAdministrator],
        path: 'project-settings',
        name: 'Project & Quote Settings',
        icon: (color?: string): ReactElement => <Setting2 variant='TwoTone' color={color} />,
        main: () => <ProjectSettings />,
      },
      {
        allowedRoles: [Enums.RoleName.SlabstackAdministrator, Enums.RoleName.ITAdministrator],
        path: 'backlog-settings',
        name: 'Backlog Settings',
        icon: (color?: string): ReactElement => <Chart21 variant='TwoTone' color={color} />,
        main: () => <BacklogSettings />,
      },
      {
        path: '*',
        main: () => <Navigate to='' />,
      },
    ],
  },
];

// All possible routes allowed within our frontend app.
//
// These should all be imported via `utils/LazyImports` to ensure that Vite
// can split out each page into its own chunk of code in production, so that
// users don't need to load more code than necessary for their actual usage.
const slabRouterSections: SlabRoutesSection[] = [
  {
    name: 'Dashboard',
    children: [
      {
        path: '',
        name: 'Dashboard',
        main: (): ReactElement => <Dashboard title='CRM' kind={Enums.DashboardKind.CRM} />,
      },
      {
        path: 'dashboard',
        icon: (color?: string): ReactElement => <Element3 variant='TwoTone' color={color} />,
        name: 'Dashboard',
        main: (): ReactElement => <Dashboard title='CRM' kind={Enums.DashboardKind.CRM} />,
      },
      {
        requiredFlags: [],
        permissions: [Enums.Permission.ViewActualsDashboard],
        path: 'backlog',
        icon: (color?: string): ReactElement => <Chart21 variant='TwoTone' color={color} />,
        name: 'Backlog',
        main: (): ReactElement => <ActualsPage />,
        obfuscatedMain: (): ReactElement => <ObfuscatedActualsPage />,
        disableObfuscation: [Enums.FeatureFlagName.FeatureFlagActualsDashboard],
        subRoutes: slabActualsRouter,
      },
      {
        requiredFlags: [Enums.FeatureFlagName.FeatureFlagPlanningRelease],
        permissions: [Enums.Permission.CanViewPlanning],
        path: 'planning',
        icon: (color?: string): ReactElement => <Calculator variant='TwoTone' color={color} />,
        name: 'Planning',
        newFeature: true,
        main: (): ReactElement => <PlanningPage />,
        obfuscatedMain: (): ReactElement => <ObfuscatedPlanningPage />,
        disableObfuscation: [Enums.FeatureFlagName.FeatureFlagPlanning],
      },
      {
        path: 'projects',
        icon: (color?: string): ReactElement => <Briefcase variant='TwoTone' color={color} />,
        name: 'Projects',
        main: (): ReactElement => <ProjectList />,
      },
      {
        path: 'projects/:id',
        main: (): ReactElement => <ProjectDetailsPage />,
      },
      {
        path: 'quotes',
        icon: (color?: string): ReactElement => <ReceiptItem variant='TwoTone' color={color} />,
        name: 'Quotes',
        main: (): ReactElement => <QuoteList />,
      },
      {
        path: 'quotes/escalation-manager',
        main: (): ReactElement => <EscalationManagerList />,
      },
      {
        path: 'quotes/:id',
        main: (): ReactElement => <QuoteDetailsPage />,
      },
      {
        requiredFlags: [Enums.FeatureFlagName.FeatureFlagCopilotChat],
        path: 'ai-chat',
        icon: (color?: string): ReactElement => <DeviceMessage variant='TwoTone' color={color} />,
        name: 'AI Assistant',
        main: (): ReactElement => <AIChatPage />,
      },
      {
        path: 'forecasts',
        icon: (color?: string): ReactElement => <Chart variant='TwoTone' color={color} />,
        name: 'Forecasts',
        main: (): ReactElement => <ForecastList />,
      },
      {
        path: 'companies',
        icon: (color?: string): ReactElement => <Buildings variant='TwoTone' color={color} />,
        name: 'Companies',
        main: (): ReactElement => <CompanyList />,
      },
      {
        path: 'companies/dispatch-customers',
        name: 'Connect Dispatch Customers',
        main: (): ReactElement => <DispatchCustomerList />,
        requiredFlags: [Enums.FeatureFlagName.FeatureFlagDispatchCustomer],
        subRoutes: slabDispatchCustomersRouter,
      },
      {
        path: 'companies/:id',
        main: (): ReactElement => <CompanyDetailsPage />,
      },
      {
        path: 'contacts',
        icon: (color?: string): ReactElement => <UserSquare variant='TwoTone' color={color} />,
        name: 'Contacts',
        main: (): ReactElement => <ContactList />,
      },
      {
        path: 'competitors',
        icon: (color?: string): ReactElement => <UserSearch variant='TwoTone' color={color} />,
        name: 'Competitors',
        main: (): ReactElement => <CompetitorsList />,
        subRoutes: slabCompetitorsRouter,
      },
      {
        path: 'contacts/:id',
        main: (): ReactElement => <ContactDetailsPage />,
      },
      {
        path: 'activities',
        icon: (color?: string): ReactElement => <Calendar variant='TwoTone' color={color} />,
        name: 'Activities',
        main: (): ReactElement => <ActivitiesView />,
      },
      {
        path: 'activities/:id',
        main: (): ReactElement => <ActivityDetailsPage />,
      },

      {
        path: 'reporting',
        icon: (color?: string): ReactElement => <Element3 variant='TwoTone' color={color} />,
        name: 'Reporting',
        main: (): ReactElement => (
          <Dashboard title='Reporting' kind={Enums.DashboardKind.Reporting} />
        ),
      },
    ],
  },
  {
    name: 'Database',
    children: [
      {
        path: 'database',
        icon: (color?: string): ReactElement => <Monitor variant='TwoTone' color={color} />,
        name: 'Database',
        main: (): ReactElement => (
          <Dashboard title='Database' kind={Enums.DashboardKind.Database} />
        ),
      },
      {
        path: 'products',
        icon: (color?: string): ReactElement => <Box1 variant='TwoTone' color={color} />,
        name: 'Products',
        main: (): ReactElement => <ProductList />,
      },
      {
        path: 'products/:id',
        main: (): ReactElement => <ProductDetailsPage />,
      },
      {
        permissions: [Enums.Permission.ViewMixDesignsSection],
        path: 'mixdesigns',
        icon: (color?: string): ReactElement => <I3Square variant='TwoTone' color={color} />,
        name: 'Mix Designs',
        main: (): ReactElement => <MixList />,
      },
      {
        permissions: [Enums.Permission.ViewMixDesignsSection],
        path: 'mixdesigns/:id',
        main: (): ReactElement => <MixDetailsPage />,
      },
      {
        permissions: [Enums.Permission.ViewMaterialsSection],
        path: 'materials',
        icon: (color?: string): ReactElement => <Brush3 variant='TwoTone' color={color} />,
        name: 'Materials',
        main: (): ReactElement => <MaterialList />,
      },
      {
        permissions: [Enums.Permission.ViewMaterialsSection],
        path: 'materials/:id',
        main: (): ReactElement => <MaterialDetailsPage />,
      },
      {
        path: 'plants',
        icon: (color?: string): ReactElement => <Buildings2 variant='TwoTone' color={color} />,
        name: 'Locations',
        main: (): ReactElement => <PlantList />,
      },
      {
        path: 'plants/:id',
        main: (): ReactElement => <PlantDetailsPage />,
      },
      {
        permissions: [Enums.Permission.ViewTruckingType],
        path: 'trucking',
        icon: (color?: string): ReactElement => <TruckFast variant='TwoTone' color={color} />,
        name: 'Trucking',
        main: (): ReactElement => <TruckingList />,
      },
    ],
  },
  {
    name: 'Admin',
    children: [
      {
        allowedRoles: [Enums.RoleName.SlabstackAdministrator, Enums.RoleName.ITAdministrator],
        path: 'admin',
        icon: (color?: string): ReactElement => <User variant='TwoTone' color={color} />,
        name: 'Admin',
        main: () => <AdminPage />,
        subRoutes: slabAdminRouterSections,
      },
      {
        path: 'projectconfigs',
        icon: (color?: string): ReactElement | null => <Notepad variant='TwoTone' color={color} />,
        name: 'Project Configs',
        main: (): ReactElement => <ProjectConfigList />,
      },
      {
        path: 'projectconfigs/:id',
        main: (): ReactElement => <ProjectConfigDetailsPage />,
      },
      {
        path: 'quoteconfigs',
        icon: (color?: string): ReactElement | null => <Notepad2 variant='TwoTone' color={color} />,
        name: 'Quote Configs',
        main: (): ReactElement => <QuoteConfigList />,
      },
      {
        path: 'quoteconfigs/:id',
        main: (): ReactElement => <QuoteConfigDetailsPage />,
      },
      {
        requiredFlags: [Enums.FeatureFlagName.FeatureFlagTierPricing],
        visible: (userInfo): boolean => userInfo.tenant.tierPricing,
        path: 'tier-pricing',
        name: 'Tier Pricing Configs',
        icon: (color?: string): ReactElement => <DollarCircle variant='TwoTone' color={color} />,
        main: (): ReactElement => <TierPricingPage />,
        subRoutes: slabTierPricingRouter,
      },
      {
        path: 'emailconfig',
        icon: (color?: string): ReactElement => <DeviceMessage variant='TwoTone' color={color} />,
        name: 'Email Config',
        main: (): ReactElement => <EmailConfigLayout />,
        subRoutes: slabEmailConfigRouterSections,
      },
      {
        requiredFlags: [Enums.FeatureFlagName.FeatureFlagReminderSystemSettings],
        path: 'notification-preferences',
        icon: (color?: string): ReactElement => <Notification variant='TwoTone' color={color} />,
        name: 'Notification Config',
        main: (): ReactElement => <NotificationPreferencesPage />,
      },
      {
        path: 'refer',
        main: (): ReactElement => <Refer />,
      },
    ],
  },
];

export const SlabRoutesValidator =
  (sections: SlabRoutesSection[]) =>
  (userInfo: UserInfo): SlabRoutesSection[] => {
    return sections
      .map((section) => {
        const enabledFlagNames = userInfo.featureFlags
          .filter((flag) => flag.isEnabled)
          .map((flag) => flag.name);

        const filteredRoutes = section.children
          .filter((route) =>
            (route.requiredFlags ?? []).every((requiredFlag) =>
              enabledFlagNames.includes(requiredFlag),
            ),
          )
          .filter(
            (route) =>
              route.allowedRoles === undefined ||
              route.allowedRoles.length === 0 ||
              route.allowedRoles.some((allowedRole) => userInfo.hasRoles([allowedRole])),
          )
          .filter((route) => route.visible === undefined || route.visible(userInfo))
          .map((route) => {
            const guarded = (page: () => ReactElement): (() => ReactElement) => {
              const BoundPage = withSlabErrorBoundary(page, { FallbackComponent: ErrorPage });
              return (): ReactElement =>
                route.permissions === undefined || userInfo.hasPermissions(route.permissions) ? (
                  <BoundPage />
                ) : (
                  <PermissionDeniedPage permissions={route.permissions} />
                );
            };

            // The router swaps obfuscatedMain in for main, so the permission
            // check has to wrap both or the teaser bypasses it.
            const guardedRoute = {
              ...route,
              main: guarded(route.main),
              ...(route.obfuscatedMain === undefined
                ? {}
                : { obfuscatedMain: guarded(route.obfuscatedMain) }),
            };

            if (hasValue(route.subRoutes) && route.subRoutes.length > 0) {
              return {
                ...guardedRoute,
                subRoutes: SlabRoutesValidator(route.subRoutes)(userInfo),
              };
            }

            return guardedRoute;
          });

        return {
          name: section.name,
          children: filteredRoutes,
        };
      })
      .filter((section) => section.children.length > 0);
  };

export const UnboundSlabRoutes = SlabRoutesValidator(slabRouterSections);
export const UnboundSlabAdminRoutes = SlabRoutesValidator(slabAdminRouterSections);
export const UnboundSlabEmailConfigRoutes = SlabRoutesValidator(slabEmailConfigRouterSections);
